import { useRouter } from "next/router";
import { useCallback, useRef, useEffect } from "react";
import {
  CREATE_KEY,
  CUSTOMER_KEY,
  FLEET_KEY,
  ORDERS_KEY,
  PACKAGES_KEY,
  QUOTES_KEY,
  SCHEDULES_KEY,
} from "./shortcutKeyConstants";

function isInputFocused(): boolean {
  const active = document.activeElement;
  if (!active) return false;
  return (
    active.tagName === "INPUT" ||
    active.tagName === "TEXTAREA" ||
    (active as HTMLElement).isContentEditable
  );
}

function isModalOpen(): boolean {
  return !!document.querySelector('[data-baseweb="modal"]');
}

function hasModifierKey(event: KeyboardEvent): boolean {
  return event.metaKey || event.ctrlKey || event.altKey || event.shiftKey;
}

const PAGE_KEYS = {
  [FLEET_KEY]: "/deliveries/fleet",
  [ORDERS_KEY]: "/deliveries/orders",
  [CREATE_KEY]: "/orders/create",
  [CUSTOMER_KEY]: "/office/customers",
  [QUOTES_KEY]: "/deliveries/quotes",
  [SCHEDULES_KEY]: "/deliveries/schedules",
  [PACKAGES_KEY]: "/deliveries/packages",
};

const COMBO_KEY = "g";
const COMBO_TIMEOUT = 1000; // ms

export const useShortcutNavigation = () => {
  const router = useRouter();
  const awaitingPageKey = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const resetCombo = () => {
    awaitingPageKey.current = false;
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (isInputFocused() || isModalOpen() || hasModifierKey(event)) return;

      if (!awaitingPageKey.current && event.key === COMBO_KEY) {
        awaitingPageKey.current = true;
        timeoutRef.current = setTimeout(resetCombo, COMBO_TIMEOUT);
        return;
      }

      if (awaitingPageKey.current && PAGE_KEYS[event.key]) {
        router.push(PAGE_KEYS[event.key]);
        resetCombo();
        return;
      }

      resetCombo();
    },
    [router]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      resetCombo();
    };
  }, [handleKeyDown]);

  // For programmatic navigation
  const goToFleet = useCallback(
    () => router.push("/deliveries/fleet"),
    [router]
  );
  const goToOrders = useCallback(
    () => router.push("/deliveries/orders"),
    [router]
  );
  const goToCreate = useCallback(() => router.push("/orders/create"), [router]);

  return { goToFleet, goToOrders, goToCreate };
};
